import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Reply, MoreVertical, Flag, Archive, Bot, Sparkles,
  Clock, User, Mail, AlertCircle, CheckCircle, ChevronDown,
  Copy, Download, Star, Trash2, Edit, Save, Send, X,
  Loader2, Zap, Brain, FileText, MessageSquare, Settings as SettingsIcon
} from 'lucide-react';
import { Thread, Message, Note, AuditLog } from '@/types';
import { getThread, assignThread, updateThreadStatus, markThreadRead, replyToThread, addNote, summarizeThread, extractActionItems, draftReply } from '@/api';
import { useThreadStore, useAuthStore, useAIStore, useUIStore } from '@/store';
import { useToast } from '@/components/ui/Toaster';
import clsx from 'clsx';
import { formatDistanceToNow, parseISO } from 'date-fns';

interface ThreadDetailData {
  thread: Thread;
  messages: Message[];
  notes: Note[];
  auditLogs: AuditLog[];
}

export default function ThreadDetail() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const { currentThread, setCurrentThread, setMessages, setNotes, setLoading: setThreadLoading, setError: setThreadError } = useThreadStore();
  const { user } = useAuthStore();
  const { rightPanelOpen, rightPanelTab, setRightPanelTab, openComposer, closeComposer, composerOpen, composerThreadId } = useUIStore();
  const { classifications, summaries, actionItems, drafts, setClassification, setSummary, setActionItems, setDraft, setLoading: setAILoading, clearThreadAI } = useAIStore();
  const { addToast } = useToast();

  const [data, setData] = useState<ThreadDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const threadIdParam = threadId || composerThreadId;

  const fetchThread = useCallback(async () => {
    if (!threadIdParam) return;
    setLoading(true);
    setThreadLoading(true);
    try {
      const result = await getThread(threadIdParam);
      if (result.ok && result.data) {
        setData(result.data);
        setCurrentThread(result.data.thread);
        setMessages(result.data.messages);
        setNotes(result.data.notes);
        setThreadError(null);
      } else {
        setThreadError(result.error || 'Failed to load thread');
        addToast(result.error || 'Failed to load thread', 'error');
        navigate('/inbox');
      }
    } catch (e) {
      setThreadError('Network error');
      addToast('Network error', 'error');
      navigate('/inbox');
    } finally {
      setLoading(false);
      setThreadLoading(false);
    }
  }, [threadIdParam, navigate, setCurrentThread, setMessages, setNotes, setThreadLoading, setThreadError, addToast]);

  useEffect(() => {
    fetchThread();
    if (threadIdParam) {
      markThreadRead(threadIdParam).catch(console.error);
    }
    return () => clearThreadAI(threadIdParam || '');
  }, [fetchThread, threadIdParam, markThreadRead, clearThreadAI]);

  if (!threadIdParam) {
    return <div className="flex items-center justify-center h-64">Select a thread to view</div>;
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-signal-600" />
      </div>
    );
  }

  if (!data) return null;

  const { thread, messages, notes, auditLogs } = data;
  const classification = classifications[threadIdParam];
  const summary = summaries[threadIdParam];
  const actions = actionItems[threadIdParam];
  const draft = drafts[threadIdParam];

  const participantEmails = JSON.parse(thread.participant_emails || '[]');
  const aiTags = JSON.parse(thread.ai_tags || '[]');

  const handleReply = async () => {
    if (!replyBody.trim() || !replyTo) return;
    setReplying(true);
    try {
      const result = await replyToThread(threadIdParam, {
        to: replyTo,
        subject: replySubject,
        body: replyBody,
        staff_id: user?.id || 'unknown',
      });
      if (result.ok) {
        addToast('Reply sent', 'success');
        closeComposer();
        setReplyBody('');
        fetchThread();
      } else {
        addToast(result.error || 'Failed to send reply', 'error');
      }
    } catch (e) {
      addToast('Network error', 'error');
    } finally {
      setReplying(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      const result = await addNote(threadIdParam, noteText);
      if (result.ok) {
        addToast('Note added', 'success');
        setNoteText('');
        fetchThread();
      } else {
        addToast(result.error || 'Failed to add note', 'error');
      }
    } catch (e) {
      addToast('Network error', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const handleAIAction = async (action: 'summarize' | 'actions' | 'draft') => {
    setAILoading(action, true);
    try {
      if (action === 'summarize') {
        const result = await summarizeThread(threadIdParam);
        if (result.ok) setSummary(threadIdParam, result.data?.summary || '');
        else addToast(result.error || 'Failed to summarize', 'error');
      } else if (action === 'actions') {
        const result = await extractActionItems(threadIdParam);
        if (result.ok) setActionItems(threadIdParam, result.data?.actions || []);
        else addToast(result.error || 'Failed to extract actions', 'error');
      } else if (action === 'draft') {
        const result = await draftReply(threadIdParam);
        if (result.ok) setDraft(threadIdParam, result.data?.draft || '');
        else addToast(result.error || 'Failed to draft reply', 'error');
      }
    } catch (e) {
      addToast('Network error', 'error');
    } finally {
      setAILoading(action, false);
    }
  };

  const handleStatusChange = async (status: Thread['status']) => {
    try {
      const result = await updateThreadStatus(threadIdParam, status);
      if (result.ok) {
        addToast(`Status updated to ${status}`, 'success');
        fetchThread();
      } else {
        addToast(result.error || 'Failed to update status', 'error');
      }
    } catch (e) {
      addToast('Network error', 'error');
    }
    setShowStatusDropdown(false);
  };

  const statusOptions: Thread['status'][] = ['new', 'assigned', 'in_progress', 'replied', 'closed', 'spam'];

  const domainNames: Record<string, string> = {
    'chiefops.co.za': 'ChiefOps',
    'goodaiglobal.org': 'GoodAI Global',
    'kekelebaka.com': 'Keke Lebaka',
  };

  return (
    <div className="h-full flex flex-col">
      {/* Thread Header */}
      <div className="border-b border-gray-200 bg-white px-4 py-3 sticky top-16 z-20">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/inbox')} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Back to inbox">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-semibold text-gray-900 truncate max-w-md sm:max-w-lg">{thread.subject || '(no subject)'}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                <span className="font-medium text-gray-700">{participantEmails[0] || 'Unknown'}</span>
                {participantEmails.length > 1 && <span>+{participantEmails.length - 1} more</span>}
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{domainNames[thread.domain] || thread.domain}</span>
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="px-2 py-0.5 rounded text-xs font-medium capitalize bg-signal-100 text-signal-700">{thread.status.replace('_', ' ')}</span>
                <span className="w-1 h-1 rounded-full bg-gray-300" />
                <span className="px-2 py-0.5 rounded text-xs font-medium capitalize bg-yellow-100 text-yellow-700">{thread.priority}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Status dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="btn-secondary"
                aria-expanded={showStatusDropdown}
              >
                <ChevronDown className="w-4 h-4" />
                <span className="hidden sm:inline">Status</span>
              </button>
              {showStatusDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowStatusDropdown(false)} />
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-50">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        className={clsx(
                          'w-full px-4 py-2 text-left text-sm',
                          thread.status === status ? 'bg-signal-50 text-signal-700' : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* AI Actions */}
            <div className="relative">
              <button className="btn-secondary" aria-label="AI Actions">
                <Bot className="w-4 h-4" />
                <span className="hidden sm:inline">AI</span>
              </button>
            </div>

            <button onClick={() => openComposer(threadIdParam)} className="btn-primary" disabled={replying}>
              <Reply className="w-4 h-4" />
              <span className="hidden sm:inline">Reply</span>
            </button>
          </div>
        </div>

        {/* AI Tags */}
        {aiTags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {aiTags.map((tag: string) => (
              <span key={tag} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Messages Panel */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                isCurrentUser={message.from_email === user?.email}
              />
            ))}

            {/* Composer inline when open */}
            {composerOpen && composerThreadId === threadIdParam && (
              <ReplyComposer
                thread={thread}
                onClose={closeComposer}
                onSend={handleReply}
                sending={replying}
                defaultBody={draft}
                defaultTo={participantEmails[0] || ''}
                defaultSubject={`Re: ${thread.subject}`}
              />
            )}
          </div>
        </div>

        {/* Right Panel */}
        {rightPanelOpen && (
          <div className="w-96 border-l border-gray-200 bg-white flex flex-col overflow-hidden">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 px-3">
              <nav className="flex gap-1" role="tablist" aria-label="Thread details">
                {[
                  { id: 'details', label: 'Details', icon: FileText },
                  { id: 'notes', label: 'Notes', icon: MessageSquare },
                  { id: 'ai', label: 'AI Assist', icon: Brain },
                  { id: 'audit', label: 'Audit', icon: SettingsIcon },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setRightPanelTab(tab.id as any)}
                    className={clsx(
                      'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      rightPanelTab === tab.id
                        ? 'bg-signal-50 text-signal-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )}
                    role="tab"
                    aria-selected={rightPanelTab === tab.id}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Panels */}
            <div className="flex-1 overflow-y-auto p-4">
              {rightPanelTab === 'details' && <DetailsPanel thread={thread} classification={classification} />}
              {rightPanelTab === 'notes' && <NotesPanel notes={notes} onAddNote={handleAddNote} noteText={noteText} setNoteText={setNoteText} adding={addingNote} />}
              {rightPanelTab === 'ai' && <AIPanel threadId={threadIdParam} summary={summary} actions={actions} draft={draft} onAction={handleAIAction} />}
              {rightPanelTab === 'audit' && <AuditPanel logs={auditLogs} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────

function MessageItem({ message, isCurrentUser }: { message: Message; isCurrentUser: boolean }) {
  const time = formatDistanceToNow(parseISO(message.received_at), { addSuffix: true });

  return (
    <div className={clsx('flex gap-3', message.direction === 'outbound' && 'flex-row-reverse')}>
      <div className={clsx('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0', isCurrentUser ? 'bg-signal-100 text-signal-700' : 'bg-gray-100 text-gray-600')}>
        {message.from_name?.charAt(0).toUpperCase() || message.from_email.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-gray-900">{message.from_name || message.from_email}</span>
          <span className="text-xs text-gray-500">{message.from_email}</span>
          <span className="text-xs text-gray-400">{time}</span>
          {message.direction === 'outbound' && <Mail className="w-3 h-3 text-signal-600" aria-label="Sent from inbox" />}
        </div>
        <div className={clsx('prose prose-sm max-w-none', message.direction === 'outbound' ? 'bg-signal-50' : 'bg-white')}>
          <pre className="whitespace-pre-wrap text-sm text-gray-700">{message.body_text}</pre>
        </div>
      </div>
    </div>
  );
}

function ReplyComposer({ thread, onClose, onSend, sending, defaultBody, defaultTo, defaultSubject }: {
  thread: Thread; onClose: () => void; onSend: () => void; sending: boolean;
  defaultBody: string; defaultTo: string; defaultSubject: string;
}) {
  const [body, setBody] = useState(defaultBody || '');
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900">Compose Reply</h3>
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200" aria-label="Close composer">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-2">
        <div>
          <label className="label">To</label>
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} className="input" />
        </div>
        <div>
          <label className="label">Subject</label>
          <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className="input" />
        </div>
        <div>
          <label className="label">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="input min-h-[150px] font-mono text-sm"
            placeholder="Type your reply... [SIGNATURE] will be replaced with your signature"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-secondary" disabled={sending}>Cancel</button>
          <button onClick={onSend} className="btn-primary" disabled={sending || !body.trim()}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <> <Send className="w-4 h-4" /> Send </>}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailsPanel({ thread, classification }: { thread: Thread; classification?: any }) {
  const participantEmails = JSON.parse(thread.participant_emails || '[]');

  return (
    <div className="space-y-4">
      <div>
        <h4 className="font-medium text-gray-900 mb-2">Thread Info</h4>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Thread ID</dt>
            <dd className="font-mono text-gray-900 truncate max-w-[200px]">{thread.thread_id}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Domain</dt>
            <dd className="text-gray-900">{thread.domain}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Participants</dt>
            <dd className="text-gray-900">{participantEmails.join(', ')}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Message Count</dt>
            <dd className="text-gray-900">{thread.message_count}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Created</dt>
            <dd className="text-gray-900">{formatDistanceToNow(parseISO(thread.created_at), { addSuffix: true })}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Last Activity</dt>
            <dd className="text-gray-900">{formatDistanceToNow(parseISO(thread.last_message_at), { addSuffix: true })}</dd>
          </div>
        </dl>
      </div>

      {classification && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-2">AI Classification</h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Category</dt>
              <dd className="text-gray-900 capitalize">{classification.category}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Priority</dt>
              <dd className="text-gray-900 capitalize">{classification.priority}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Sentiment</dt>
              <dd className="text-gray-900 capitalize">{classification.sentiment}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Confidence</dt>
              <dd className="text-gray-900">{Math.round(classification.confidence * 100)}%</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Spam Score</dt>
              <dd className="text-gray-900">{Math.round(classification.spam_score * 100)}%</dd>
            </div>
            {classification.action_items?.length > 0 && (
              <div>
                <dt className="text-gray-500">Action Items</dt>
                <dd className="mt-1 space-y-1">
                  {classification.action_items.map((item: string, i: number) => (
                    <p key={i} className="text-sm text-gray-700 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-signal-600" />
                      {item}
                    </p>
                  ))}
                </dd>
              </div>
            )}
          </dl>
        </div>
      )}
    </div>
  );
}

function NotesPanel({ notes, onAddNote, noteText, setNoteText, adding }: {
  notes: Note[]; onAddNote: () => void; noteText: string; setNoteText: (v: string) => void; adding: boolean;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="space-y-3 mb-4">
        {notes.map((note) => (
          <div key={note.note_id} className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{note.staff_id}</span>
              <span>{formatDistanceToNow(parseISO(note.created_at), { addSuffix: true })}</span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.note}</p>
          </div>
        ))}
        {notes.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No notes yet</p>}
      </div>

      <div className="border-t border-gray-200 pt-4">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          className="input min-h-[80px] mb-2"
          placeholder="Add internal note..."
        />
        <button onClick={onAddNote} disabled={adding || !noteText.trim()} className="btn-primary w-full">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Note'}
        </button>
      </div>
    </div>
  );
}

function AIPanel({ threadId, summary, actions, draft, onAction }: {
  threadId: string | undefined; summary?: string; actions?: string[]; draft?: string; onAction: (action: 'summarize' | 'actions' | 'draft') => void;
}) {
  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-gray-900">Thread Summary</h4>
          <button onClick={() => threadId && onAction('summarize')} className="btn-secondary text-xs">
            <Sparkles className="w-3 h-3" /> Generate
          </button>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 min-h-[100px]">
          {summary ? <p className="text-sm text-gray-700">{summary}</p> : <p className="text-sm text-gray-500 italic">Click Generate to create AI summary</p>}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-gray-900">Action Items</h4>
          <button onClick={() => threadId && onAction('actions')} className="btn-secondary text-xs">
            <Zap className="w-3 h-3" /> Extract
          </button>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 min-h-[100px]">
          {actions?.length ? (
            <ul className="space-y-2">
              {actions.map((action, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                  {action}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic">Click Extract to find action items</p>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-gray-900">Draft Reply</h4>
          <button onClick={() => threadId && onAction('draft')} className="btn-secondary text-xs">
            <Zap className="w-3 h-3" /> Draft
          </button>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 min-h-[100px]">
          {draft ? <p className="text-sm text-gray-700 whitespace-pre-wrap">{draft}</p> : <p className="text-sm text-gray-500 italic">Click Draft to generate reply</p>}
        </div>
      </div>
    </div>
  );
}

function AuditPanel({ logs }: { logs: AuditLog[] }) {
  return (
    <div className="space-y-2">
      {logs.map((log) => (
        <div key={log.audit_id} className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span className="font-medium text-gray-700">{log.action}</span>
            <span>{formatDistanceToNow(parseISO(log.timestamp), { addSuffix: true })}</span>
          </div>
          {log.metadata && (
            <pre className="text-xs text-gray-600 bg-white p-2 rounded overflow-auto max-h-32">{log.metadata}</pre>
          )}
        </div>
      ))}
      {logs.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No audit logs</p>}
    </div>
  );
}